import { isMobile } from 'react-device-detect';
import { isNativeAndroid, isNativeSocialLoginBridgeAvailable, sendToNative } from './NativeUtil';
import { loadScript } from './loadScript';
import type { SocialLoginCredentialInput } from '../types/socialLoginTypes';

const KAKAO_SDK_SCRIPT_ID = 'kakao-sdk-script';
const KAKAO_SDK_SCRIPT_SRC = 'https://developers.kakao.com/sdk/js/kakao.min.js';

const NATIVE_KAKAO_TIMEOUT_MS = 60000;
const NATIVE_KAKAO_TIMEOUT_ANDROID_MS = 25000;

let kakaoScriptPromise: Promise<void> | null = null;

function loadKakaoScript() {
  if (!kakaoScriptPromise) {
    kakaoScriptPromise = loadScript(KAKAO_SDK_SCRIPT_ID, KAKAO_SDK_SCRIPT_SRC);
  }
  return kakaoScriptPromise;
}

function getKakaoJavaScriptKey() {
  return (
    import.meta.env.REACT_APP_KAKAO_JAVA_SCRIPT_KEY || import.meta.env.REACT_APP_KAKAO_JAVASCRIPT_KEY || ''
  );
}

function initKakaoSdk(appKey: string): boolean {
  if (!window.Kakao) return false;
  if (!window.Kakao.isInitialized()) {
    window.Kakao.init(appKey);
  }
  return true;
}

export function loginWithKakaoAccessToken(): Promise<SocialLoginCredentialInput | null> {
  const appKey = getKakaoJavaScriptKey();
  if (!appKey) return Promise.resolve(null);

  return loadKakaoScript()
    .then(() => {
      if (!window.Kakao) return null;

      if (!initKakaoSdk(appKey)) return null;

      return new Promise<SocialLoginCredentialInput | null>((resolve) => {
        const preferKakaoTalkApp = isMobile || isNativeAndroid();
        window.Kakao?.Auth.login({
          scope: 'account_email',
          throughTalk: preferKakaoTalkApp,
          success: (authObj) => {
            if (authObj?.access_token) {
              resolve({ accessToken: authObj.access_token });
              return;
            }
            resolve(null);
          },
          fail: () => {
            resolve(null);
          }
        });
      });
    })
    .catch(() => null);
}

export function logoutKakaoWeb(): Promise<void> {
  const appKey = getKakaoJavaScriptKey();
  if (!appKey) {
    return Promise.resolve();
  }

  return loadKakaoScript()
    .then(async () => {
      if (!window.Kakao) {
        return;
      }
      if (!initKakaoSdk(appKey)) {
        return;
      }
      try {
        await window.Kakao.Auth.logout();
      } catch {}
    })
    .catch(() => {});
}

export function getCredentialFromNativeKakao(): Promise<SocialLoginCredentialInput | null> {
  if (!isNativeSocialLoginBridgeAvailable()) {
    return Promise.resolve(null);
  }

  const timeoutMs = isNativeAndroid() ? NATIVE_KAKAO_TIMEOUT_ANDROID_MS : NATIVE_KAKAO_TIMEOUT_MS;

  return new Promise<SocialLoginCredentialInput | null>((resolve) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        provider: string;
        status: 'success' | 'cancel' | 'error';
        accessToken?: string;
        errorCode?: string;
        errorMessage?: string;
        timestamp: number;
      }>;
      const result = customEvent.detail;

      if (result.provider !== 'kakao') return;

      cleanup();

      if (result.status === 'success' && result.accessToken) {
        resolve({ accessToken: result.accessToken });
        return;
      }

      resolve(null);
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('native:socialLoginResult', handler as EventListener);
    };

    window.addEventListener('native:socialLoginResult', handler as EventListener);
    sendToNative(JSON.stringify({ type: 'nativeSocialLogin', provider: 'kakao' }));
  });
}
